type Point = [number, number]; // [x, y]
type Section = [Point, Point];

const reverse = ([x, y]: Point): Point => [y, x];
const linear = ([x1, y1]: Point, [x2, y2]: Point) => (x: number) =>
  y1 + ((x - x1) / (x2 - x1)) * (y2 - y1);

export type ScaleBreakpoint = Point;
/**
 * Provide a `scale` function whose graph is a straight line with breakpoints,
 *  at which the line changes it's angle between the axis. I. e.:
 *
 *               / x, if x < 40,
 *  y = f(x) =  { 2 * x, if x >= 40 and x < 70,
 *              \ 3.5 * x, if x >= 70,
 *
 * Handy to use with Slider component.
 * Example breakpoints:
 *   const breakpoints: ScaleBreakpoint[] = [
 *     [0, 15],
 *     [40, 50],
 *     [70, 100],
 *     [100, 200],
 *   ];
 *
 * Breakpoints must be always increasing (both x and y).
 *
 * @param {ScaleBreakpoint[]} points
 */
export const scaleWithBreakpoints = (points: ScaleBreakpoint[]) => {
  const [xMin, yMin] = points[0];
  const [xMax, yMax] = points[points.length - 1];

  const sections = points.reduce<Section[]>(
    (sections, point, index) =>
      index === points.length - 1 ? sections : [...sections, [point, points[index + 1]]],
    []
  );

  const firstSection = sections[0];
  const lastSection = sections[sections.length - 1];

  const findSectionByX = (x: number) => {
    if (x <= xMin) return firstSection;
    if (x >= xMax) return lastSection;
    return sections.find(([[x1], [x2]]) => x >= x1 && x <= x2) || firstSection;
  };

  const findSectionByY = (y: number) => {
    if (y <= yMin) return firstSection;
    if (y >= yMax) return lastSection;
    return sections.find(([[_1, y1], [_2, y2]]) => y >= y1 && y <= y2) || firstSection;
  };

  return {
    scale: (x: number) => {
      const [point1, point2] = findSectionByX(x);
      return linear(point1, point2)(x);
    },
    descale: (y: number) => {
      const [point1, point2] = findSectionByY(y);
      return linear(reverse(point1), reverse(point2))(y);
    },
  };
};
